import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Select,
  useColorModeValue,
  useToast,
  Spinner,
  Box,
  Image,
  IconButton,
  Badge,
  Divider,
  SimpleGrid,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Flex,
  Grid
} from '@chakra-ui/react';
import { FiUpload, FiX, FiSave, FiFileText } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import { updateContent, getContentForEdit, ContentUpdateData } from '../services/contentUpdateService';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';

interface EditBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  userId: string;
  onSuccess: () => void;
}

const EditBookModal: React.FC<EditBookModalProps> = ({
  isOpen,
  onClose,
  contentId,
  userId,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [updateData, setUpdateData] = useState<ContentUpdateData>({});
  const [previewUrls, setPreviewUrls] = useState<{ [key: string]: string }>({});
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const inputBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (isOpen && contentId) {
      loadContent();
    }
  }, [isOpen, contentId]);

  const loadContent = async () => {
    setIsLoading(true);
    try {
      const contentData = await getContentForEdit(contentId, userId);
      setContent(contentData);
      setUpdateData({
        title: contentData.title || '',
        description: contentData.description || '',
        author: (contentData as any).author || '',
        genres: contentData.genres || [],
        rating: (contentData as any).rating || 'All Ages',
        language: contentData.language || 'English',
        tags: (contentData as any).tags || '',
        cast: (contentData as any).cast || '',
        director: (contentData as any).director || '',
        writer: (contentData as any).writer || ''
      });
    } catch (error) {
      console.error('Error loading content:', error);
      toast({
        title: 'Error',
        description: 'Failed to load content for editing.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof ContentUpdateData, value: any) => {
    setUpdateData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (field: keyof ContentUpdateData, file: File) => {
    setUpdateData(prev => ({
      ...prev,
      [field]: file
    }));
    
    const previewUrl = URL.createObjectURL(file);
    setPreviewUrls(prev => ({
      ...prev,
      [field]: previewUrl
    }));
  };

  const handleGenreToggle = (genre: string) => {
    setUpdateData(prev => {
      const currentGenres = prev.genres || [];
      if (currentGenres.includes(genre)) {
        return { ...prev, genres: currentGenres.filter(g => g !== genre) };
      } else if (currentGenres.length < 3) {
        return { ...prev, genres: [...currentGenres, genre] };
      }
      return prev;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updateContent(contentId, userId, updateData);
      
      if (result.success) {
        toast({
          title: 'Book Updated',
          description: result.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: 'Update Failed',
          description: result.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: 'Update Failed',
        description: 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const labelColor = useColorModeValue('gray.700', 'gray.300');

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Spinner size="xl" />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "7xl" }}>
      <ModalOverlay zIndex={1400} />
      <ModalContent
        maxH={{ base: "100vh", md: "85vh" }}
        mt={{ base: "0", md: "20px" }}
        mx={{ base: "0", md: "auto" }}
        overflowY="auto"
        zIndex={1500}
        borderRadius={{ base: "none", md: "lg" }}
        position="relative"
      >
        <ModalHeader 
          fontSize={{ base: "lg", md: "xl" }}
          pt={{ base: "80px", md: "6" }}
          pb={{ base: "4", md: "6" }}
        >
          Edit Book
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={{ base: 20, md: 6 }}>
          <Box>
            {/* Top Section - Title, Author, Description */}
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6} mb={6}>
              <VStack gap={4} align="stretch">
                <FormControl>
                  <FormLabel color={labelColor} fontWeight="bold">
                    Book Title *
                  </FormLabel>
                  <Input
                    value={updateData.title || ''}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter book title"
                    bg={inputBg}
                    borderColor={borderColor}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel color={labelColor} fontWeight="bold">
                    Author
                  </FormLabel>
                  <Input
                    value={updateData.author || ''}
                    onChange={(e) => handleInputChange('author', e.target.value)}
                    placeholder="Enter author name"
                    bg={inputBg}
                    borderColor={borderColor}
                  />
                </FormControl>
              </VStack>
              
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Description *
                </FormLabel>
                <Textarea
                  value={updateData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter book description"
                  bg={inputBg}
                  borderColor={borderColor}
                  rows={6}
                />
              </FormControl>
            </Grid>

            {/* Genres Section */}
            <FormControl mb={6}>
              <FormLabel color={labelColor} fontWeight="bold">
                Genres * (Select up to 3)
              </FormLabel>
              <Flex wrap="wrap" gap={2}>
                {GENRES.map(genre => (
                  <Button
                    key={genre}
                    size="sm"
                    variant={(updateData.genres || []).includes(genre) ? 'solid' : 'outline'}
                    colorScheme="purple"
                    onClick={() => handleGenreToggle(genre)}
                    isDisabled={!(updateData.genres || []).includes(genre) && (updateData.genres || []).length >= 3}
                  >
                    {genre}
                  </Button>
                ))}
              </Flex>
            </FormControl>

            {/* Middle Section - Characters, Editor, Writer */}
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={4} mb={6}>
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Characters
                </FormLabel>
                <Input
                  value={updateData.cast || ''}
                  onChange={(e) => handleInputChange('cast', e.target.value)}
                  placeholder="Character names (comma separated)"
                  bg={inputBg}
                  borderColor={borderColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Editor
                </FormLabel>
                <Input
                  value={updateData.director || ''}
                  onChange={(e) => handleInputChange('director', e.target.value)}
                  placeholder="Editor name(s)"
                  bg={inputBg}
                  borderColor={borderColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Writer
                </FormLabel>
                <Input
                  value={updateData.writer || ''}
                  onChange={(e) => handleInputChange('writer', e.target.value)}
                  placeholder="Writer name(s)"
                  bg={inputBg}
                  borderColor={borderColor}
                />
              </FormControl>
            </Grid>

            {/* Bottom Section - Rating, Language, Tags */}
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={4} mb={6}>
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Content Rating
                </FormLabel>
                <RadioGroup
                  value={updateData.rating || 'All Ages'}
                  onChange={(value) => handleInputChange('rating', value)}
                >
                  <HStack gap={2} wrap="wrap">
                    {CONTENT_RATINGS.map(rating => (
                      <Radio key={rating.value} value={rating.value} colorScheme="purple" size="sm">
                        {rating.label}
                      </Radio>
                    ))}
                  </HStack>
                </RadioGroup>
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Language
                </FormLabel>
                <Select
                  value={updateData.language || 'English'}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                  bg={inputBg}
                  borderColor={borderColor}
                >
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="German">German</option>
                  <option value="Chinese">Chinese</option>
                  <option value="Japanese">Japanese</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Tags
                </FormLabel>
                <Input
                  value={updateData.tags || ''}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  placeholder="Enter tags (comma separated)"
                  bg={inputBg}
                  borderColor={borderColor}
                />
              </FormControl>
            </Grid>

            {/* File Upload Section */}
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6} mb={6}>
              {/* Book Cover */}
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Book Cover
                </FormLabel>
                <Box
                  border="2px dashed"
                  borderColor={previewUrls.coverFile ? 'purple.300' : borderColor}
                  borderRadius="lg"
                  p={6}
                  textAlign="center"
                  bg={previewUrls.coverFile ? 'purple.50' : inputBg}
                  _dark={{ bg: previewUrls.coverFile ? 'purple.900' : 'gray.700' }}
                  cursor="pointer"
                  _hover={{ borderColor: 'purple.400' }}
                  transition="all 0.2s"
                  onClick={() => document.getElementById('cover-upload')?.click()}
                  minH="180px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {previewUrls.coverFile ? (
                    <VStack gap={3}>
                      <Box position="relative">
                        <Image
                          src={previewUrls.coverFile}
                          alt="Cover preview"
                          maxH="120px"
                          maxW="160px"
                          objectFit="cover"
                          borderRadius="md"
                        />
                        <Button
                          position="absolute"
                          top={-2}
                          right={-2}
                          size="sm"
                          colorScheme="red"
                          variant="solid"
                          borderRadius="full"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUpdateData(prev => ({ ...prev, coverFile: undefined }));
                            setPreviewUrls(prev => {
                              const newUrls = { ...prev };
                              delete newUrls.coverFile;
                              return newUrls;
                            });
                          }}
                        >
                          <IconWrapper icon={FiX} size={12} />
                        </Button>
                      </Box>
                      <Text fontSize="sm" color="gray.600">
                        Click to change cover
                      </Text>
                    </VStack>
                  ) : (
                    <VStack gap={3}>
                      <IconWrapper icon={FiUpload} size={28} color="gray.400" />
                      <Text fontSize="sm" color="gray.600">
                        Upload book cover
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        JPG, PNG, WEBP (Max 200MB)
                      </Text>
                    </VStack>
                  )}
                  <Input
                    id="cover-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload('coverFile', file);
                    }}
                    display="none"
                  />
                </Box>
              </FormControl>

              {/* Book File */}
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Book File * (PDF only)
                </FormLabel>
                <Box
                  border="2px dashed"
                  borderColor={previewUrls.bookFile ? 'purple.300' : borderColor}
                  borderRadius="lg"
                  p={6}
                  bg={previewUrls.bookFile ? 'purple.50' : inputBg}
                  _dark={{ bg: previewUrls.bookFile ? 'purple.900' : 'gray.700' }}
                  minH="180px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <VStack gap={4} w="100%">
                    <IconWrapper icon={FiFileText} size={32} color="gray.400" />
                    <VStack gap={2}>
                      <Text color={labelColor} fontSize="sm" textAlign="center">
                        {previewUrls.bookFile ? (updateData.bookFile as File)?.name || 'Book file selected' : 'No file selected'}
                      </Text>
                      <HStack>
                        {previewUrls.bookFile ? (
                          <Button size="sm" colorScheme="red" variant="outline" onClick={() => {
                            setUpdateData(prev => ({ ...prev, bookFile: undefined }));
                            setPreviewUrls(prev => {
                              const newUrls = { ...prev };
                              delete newUrls.bookFile;
                              return newUrls;
                            });
                          }}>
                            Remove
                          </Button>
                        ) : (
                          <Button size="sm" colorScheme="purple" onClick={() => document.getElementById('book-file-edit-upload')?.click()}>
                            Select File
                          </Button>
                        )}
                      </HStack>
                    </VStack>
                  </VStack>
                  <Input
                    id="book-file-edit-upload"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload('bookFile', file);
                    }}
                    display="none"
                  />
                </Box>
              </FormControl>
            </Grid>
          </Box>
        </ModalBody>
        <ModalFooter 
          position={{ base: "fixed", md: "relative" }}
          bottom={{ base: 0, md: "auto" }}
          left={{ base: 0, md: "auto" }}
          right={{ base: 0, md: "auto" }}
          bg={{ base: "gray.800", md: "transparent" }}
          borderTop={{ base: "1px solid", md: "none" }}
          borderColor={{ base: "gray.600", md: "transparent" }}
          p={{ base: 4, md: 6 }}
          zIndex={1600}
        >
          <HStack width="100%" justify="space-between" gap={3}>
            <Button 
              variant="ghost" 
              onClick={onClose}
              size={{ base: "md", md: "sm" }}
              flex={{ base: 1, md: "none" }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              variant="outline"
              size={{ base: "md", md: "sm" }}
              flex={{ base: 1, md: "none" }}
            >
              Delete Book
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="Saving..."
              leftIcon={<IconWrapper icon={FiSave} size={16} />}
              size={{ base: "md", md: "sm" }}
              flex={{ base: 1, md: "none" }}
            >
              Save Changes
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditBookModal;
